import React, { useState, useRef } from 'react';
import SVG from 'react-inlinesvg';
import classNames from 'classnames/bind';
import { ShadowStyle } from 'consts/shadow';
import { getInputBorderStyle } from 'lib/BaseInput';
import { InputShape, InputTheme } from 'components/common/types/Input.types';
import { useOutsideClick } from 'customHooks/useOutsideClick';
import { Option } from 'components/common/types/DropdownItem.types';
import styles from 'lib/AsyncSelect/AsyncSelect.pcss';
import chevronDownSvg from 'assets/icons/arrow_light_down.svg';
import DropdownItem from 'lib/MultiLevelDropdown/DropdownItem';

import Chip from 'lib/Chip';
import { Color } from 'lib/Chip/Chip.types';

const cx = classNames.bind(styles);

interface BaseSelectMultiLevelProps {
  options: Option[];
  value?: string[];
  onChangeItem?: (node: Option, checked: boolean) => void;
  isDisabled?: boolean;
  dataTestId?: string;
  icon: string;
  placeholder?: string;
  classNameWrapper?: string;
  shadow?: ShadowStyle;
  shape?: InputShape;
  theme?: InputTheme;
  selectedLabel?: string;
}

export const getFilteredOptions = (options: Option[], search: string): Option[] => {
  if (!search || !search.trim()) return options;

  const filterRecursive = (opts: Option[]): Option[] => {
    return opts.reduce((acc: Option[], option) => {
      const matchesSearch = option.name.toLowerCase().includes(search.trim().toLowerCase());
      const filteredChildren = option.children ? filterRecursive(option.children) : [];

      if (filteredChildren.length > 0 || matchesSearch) {
        acc.push({
          ...option,
          selectable: option.selectable,
          children: matchesSearch && option.children ? option.children : filteredChildren,
        });
      }

      return acc;
    }, []);
  };

  return filterRecursive(options);
};

const BaseSelectMultiLevel: React.FC<BaseSelectMultiLevelProps> = ({
  options,
  value = [],
  onChangeItem,
  isDisabled = false,
  dataTestId,
  icon,
  placeholder = 'Select...',
  classNameWrapper = 'w-56',
  shadow = ShadowStyle.SMALL,
  shape = InputShape.ROUNDED_MEDIUM,
  theme = InputTheme.LIGHT,
  selectedLabel = 'Selected',
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState<string | number | null>(null);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef() as React.MutableRefObject<HTMLDivElement>;

  useOutsideClick(dropdownRef, () => {
    setOpenSubMenu(null);
    setIsDropdownOpen(false);
    setSearch('');
  });

  const filteredOptions = getFilteredOptions(options, search);

  const handleToggleDropdown = (): void => {
    if (!isDisabled) {
      setIsDropdownOpen(!isDropdownOpen);
      setSearch('');
    }
  };

  const getSelectedText = (): string | React.ReactElement => {
    if (value.length === 0) return placeholder;
    return (
      <div className="flex items-center gap-2">
        <span>{selectedLabel}</span>
        <Chip label={value.length.toString()} color={Color.PRIMARY} />
      </div>
    );
  };

  return (
    <div ref={dropdownRef} className={cx('relative', classNameWrapper)}>
      <div
        className={cx(
          'min-h-[38px]',
          { 'shadow-inner-sm': shadow !== ShadowStyle.NONE, 'shadow-none': shadow === ShadowStyle.NONE },
          getInputBorderStyle({
            isDisabled,
            shape,
            theme,
          }),
        )}
        data-test-id={dataTestId}
      >
        <div
          className={cx('w-full default', {
            'async-select-shadow': shadow === ShadowStyle.SMALL,
            'async-select': theme === InputTheme.LIGHT,
            'async-select-flat-gray': theme === InputTheme.FLAT_GRAY,
          })}
        >
          <div
            className={cx('react-select__control min-h-9', {
              'react-select__control--is-disabled': isDisabled,
              'react-select__control--is-focused': isDropdownOpen,
              'cursor-not-allowed': isDisabled,
              'cursor-pointer': !isDisabled,
            })}
            onClick={handleToggleDropdown}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggleDropdown();
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div className="flex items-center w-full h-full">
              <SVG className="fill-current w-4 h-4 text-neutral-600 mr-1 flex-shrink-0" src={icon} />
              <div className="react-select__value-container flex-1 flex items-center min-h-8">
                {value.length === 0 ? (
                  <div className={cx('react-select__placeholder', 'text-neutral-600')}>{placeholder}</div>
                ) : (
                  <div className={cx('react-select__single-value', 'text-neutral-950')}>{getSelectedText()}</div>
                )}
              </div>
              <div className="react-select__indicators flex items-center">
                <div className="react-select__indicator">
                  <SVG
                    src={chevronDownSvg}
                    className={cx('transform transition-transform fill-neutral-600', {
                      'rotate-180': isDropdownOpen,
                    })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isDropdownOpen && (
        <div className="absolute top-full left-0 right-0 z-50 bg-white border border-neutral-200 rounded-md shadow-lg mt-1 min-w-full">
          <div className="px-3 pt-3 pb-2">
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={({ target }) => setSearch(target.value)}
                placeholder="Search"
                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {filteredOptions.length ? (
            <ul className="py-2 max-h-72 overflow-y-auto overflow-x-hidden">
              {filteredOptions.map((option) => (
                <DropdownItem
                  multiLevelDropdownId="base-select-multi-level"
                  option={option}
                  key={`base-select-multi-level-${option.code}`}
                  setIsSubMenuOpen={(code) => setOpenSubMenu(code)}
                  isSubMenuOpen={openSubMenu === option.code}
                  isSelectable={option.selectable}
                  selectedItems={value}
                  onChangeSelectedItem={onChangeItem}
                  isPositionFixed={false}
                  depth={1}
                />
              ))}
            </ul>
          ) : (
            <p className="text-center px-5 py-2.5 text-sm text-neutral-600">No results found</p>
          )}
        </div>
      )}
    </div>
  );
};

export default BaseSelectMultiLevel;
